import { useCallback, useEffect, useRef, useState } from 'react';
import { getSelectionCenter, normalizeAngleDelta, rotateSelection, snapAngleToStep } from './rotationUtils';
import { getPointerAngle, getSelectionClientMetrics } from './rotationHandleUtils';

const useSelectionRotation = ({ json, mode, pz, readonly, selectedIds, setJsonOutput }) => {
    const [isRotating, setIsRotating] = useState(false);
    const rotationStateRef = useRef(null);
    const setJsonOutputRef = useRef(setJsonOutput);
    const panzoomRef = useRef(pz);

    useEffect(() => {
        setJsonOutputRef.current = setJsonOutput;
        panzoomRef.current = pz;
    }, [setJsonOutput, pz]);

    const applyRotationFromPointer = useCallback((ev) => {
        const state = rotationStateRef.current;
        if (!state) return;

        const currentAngle = getPointerAngle(ev.clientX, ev.clientY, state.clientCenter);
        const delta = normalizeAngleDelta(currentAngle - state.lastPointerAngle);
        state.accumulatedAngle += delta;
        state.lastPointerAngle = currentAngle;

        const snappedAngle = snapAngleToStep(state.accumulatedAngle);
        if (snappedAngle === state.appliedAngle) return;

        state.appliedAngle = snappedAngle;
        setJsonOutputRef.current(
            rotateSelection(state.initialJson, state.selectedIds, snappedAngle, state.selectionCenter),
            false
        );
    }, []);

    const finishRotation = useCallback((ev) => {
        const state = rotationStateRef.current;
        if (!state) return;

        if (ev) applyRotationFromPointer(ev);

        const appliedAngle = state.appliedAngle;
        if (panzoomRef.current) panzoomRef.current.resume();
        rotationStateRef.current = null;
        setIsRotating(false);

        if (appliedAngle !== 0) {
            setJsonOutputRef.current(prev => prev, true);
        }
    }, [applyRotationFromPointer]);

    useEffect(() => {
        if (!isRotating) return;

        const handlePointerMove = (ev) => applyRotationFromPointer(ev);
        const handlePointerUp = (ev) => finishRotation(ev);

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
            if (rotationStateRef.current && panzoomRef.current) {
                panzoomRef.current.resume();
            }
        };
    }, [isRotating, applyRotationFromPointer, finishRotation]);

    const handleRotationStart = useCallback((ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        if (readonly || mode !== 'transform' || !selectedIds?.length) return;

        const metrics = getSelectionClientMetrics(json, selectedIds);
        if (!metrics) return;

        const selectedBoxIds = metrics.ids.filter(id => json?.[id]?.box);
        const selectionCenter = getSelectionCenter(json, selectedBoxIds);
        if (!selectionCenter || selectedBoxIds.length === 0) return;

        if (ev.currentTarget.setPointerCapture) {
            ev.currentTarget.setPointerCapture(ev.pointerId);
        }

        if (pz) pz.pause();

        rotationStateRef.current = {
            initialJson: json,
            selectedIds: selectedBoxIds,
            selectionCenter,
            clientCenter: metrics.center,
            lastPointerAngle: getPointerAngle(ev.clientX, ev.clientY, metrics.center),
            accumulatedAngle: 0,
            appliedAngle: 0
        };
        setIsRotating(true);
    }, [json, mode, pz, readonly, selectedIds]);

    return {
        isRotating,
        isRotationInProgress: () => Boolean(rotationStateRef.current),
        handleRotationStart
    };
};

export default useSelectionRotation;
